import React, { useState } from 'react';
import { PageTemplate } from '../components/PageTemplate';
import { Card, Button } from '../components/UnifiedUIComponents';
import { FaRobot, FaChartLine, FaSearch, FaComments } from 'react-icons/fa';
import { aiAPI } from '../services/api';
import useAIAction from '../hooks/useAIAction';
import AIResultPanel from '../components/AIResultPanel';

const REPORT_TYPES = [
  { key: 'results', label: 'Results Summary' },
  { key: 'ccv', label: 'CCV Trend' },
  { key: 'batch_compliance', label: 'Pending Approvals / Batch Compliance' },
];

const REVIEW_SCOPES = [
  { key: 'dashboard', label: 'Overall (pending approvals, training, CCV)' },
  { key: 'results', label: 'Results' },
  { key: 'ccv', label: 'CCV' },
  { key: 'training', label: 'Training' },
  { key: 'audit', label: 'Audit Trail' },
];

const AIAssistant = () => {
  const ai = useAIAction();
  const [instructions, setInstructions] = useState('');

  return (
    <PageTemplate
      title="AI Assistant"
      subtitle="Draft reports, review data for anomalies, and chat with your app's data. Contextual AI actions (protocol drafting, risk review, grouping suggestions) are available directly on Product Master, Equipment Trains, and Risk Assessment pages."
      icon={FaRobot}
    >
      <Card style={{ marginBottom: '1.25rem' }}>
        <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Additional instructions / corrections (optional)</label>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="e.g. focus only on failing results, ignore the training data, use a shorter executive summary, re-check the CCV trend for batch B-1042..."
        />
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)', marginBottom: 0, marginTop: '0.4rem' }}>
          This is added on top of the data-driven prompt below — use it to redirect, correct, or narrow what the AI produces for the action you click.
        </p>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>
        <Card>
          <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FaChartLine /> Draft a Report</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>AI drafts a narrative report from live data. Always reviewed before use.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {REPORT_TYPES.map((r) => (
              <Button key={r.key} variant="outline" onClick={() => ai.run(`AI Draft Report — ${r.label}`, () => aiAPI.draftReport({ reportType: r.key, instructions: instructions.trim() || undefined }), 'report')}>
                {r.label}
              </Button>
            ))}
          </div>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FaSearch /> Review Data</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>AI scans live data for anomalies, overdue items, and out-of-trend results.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {REVIEW_SCOPES.map((s) => (
              <Button key={s.key} variant="outline" onClick={() => ai.run(`AI Data Review — ${s.label}`, () => aiAPI.reviewData(s.key, instructions.trim() || undefined))}>
                {s.label}
              </Button>
            ))}
          </div>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FaComments /> Chat with App Data</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>
            Use the floating AI button in the bottom-right corner of any page to ask questions about products, equipment, risk assessments, results, and more — grounded in your live data.
          </p>
        </Card>
      </div>

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType={ai.docType}
        exportFileName="ai-assistant-output.pdf"
      />
    </PageTemplate>
  );
};

export default AIAssistant;
